import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, ShoppingBag, ChevronRight } from 'lucide-react';

// Hotspot data with product info - Dynamic from Shopify
const defaultHotspots = [
  {
    id: 'taylor-suit',
    x: 32,
    y: 22,
    name: 'THE TAYLOR SUIT',
    label: 'The Power Silhouette',
    subtitle: 'The Architectural Standard',
    price: 348,
    description: 'A masterclass in structure and poise. The Taylor Suit is engineered for the individual who demands a presence that precedes them. With a silhouette that balances modern lines with classic authority, it is the definitive uniform for high-stakes environments and decisive moments.',
    feature: 'Premium flex material with unparalleled stretch',
    link: 'https://prsvr.store/products/taylor-suit',
    handle: 'taylor-suit',
  },
  {
    id: 'taylor-jacket',
    x: 28,
    y: 42,
    name: 'THE TAYLOR JACKET',
    label: 'The Versatile Professional',
    subtitle: 'Precision Outerwear',
    price: 248,
    description: 'The intersection of comfort and command. This jacket offers a refined, structured fit that maintains its integrity from the boardroom to the lounge. A versatile essential for the modern professional who refuses to choose between technical performance and aesthetic luxury.',
    feature: 'Signature flex material with custom-cut lapels',
    link: 'https://prsvr.store/products/the-taylor-jacket',
    handle: 'the-taylor-jacket',
  },
  {
    id: 'taylor-pants',
    x: 35,
    y: 65,
    name: 'THE TAYLOR PANTS',
    label: 'The Tailored Edge',
    subtitle: 'Ergonomic Sophistication',
    price: 188,
    description: 'Tailoring evolved. These pants are cut to provide a sharp, clean line while allowing for effortless movement. Crafted with flexible Glen Plaid material and a branded zipper for a touch of luxury.',
    feature: 'Glen Plaid flex fabric with branded zipper',
    link: 'https://prsvr.store/products/the-taylor-pants-1',
    handle: 'the-taylor-pants-1',
  },
  {
    id: 'valley-duffle',
    x: 18,
    y: 72,
    name: 'THE VALLEY DUFFLE',
    label: 'The Executive Vessel',
    subtitle: 'The Global Command Center',
    price: 200,
    description: 'A legacy piece for the modern traveler. Hand-crafted for those who carry their world with them, the Valley Duffle is the sophisticated alternative to standard luggage. Premium materials with gold 3D logo plate and pyramid bag feet.',
    feature: 'Full-grain leather with gold hardware',
    link: 'https://prsvr.store/products/the-valley-duffle',
    handle: 'the-valley-duffle',
  },
];

const ProspectCapsuleSection = () => {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hotspots, setHotspots] = useState(defaultHotspots);

  // Check if mobile on mount and fetch real prices
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Fetch real prices from Shopify
    const fetchPrices = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const updatedHotspots = await Promise.all(
          defaultHotspots.map(async (hotspot) => {
            try {
              const res = await fetch(`${API_URL}/api/shopify/products/${hotspot.handle}`);
              const data = await res.json();
              if (data.success && data.product) {
                const price = data.product.variants?.[0]?.price || data.product.price || hotspot.price;
                return { ...hotspot, price };
              }
            } catch (e) {
              console.log(`Could not fetch price for ${hotspot.handle}`);
            }
            return hotspot;
          })
        );
        setHotspots(updatedHotspots);
      } catch (e) {
        console.log('Error fetching product prices:', e);
      }
    };

    fetchPrices();
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleHotspotClick = (hotspot) => {
    if (activeHotspot?.id === hotspot.id) {
      setActiveHotspot(null);
    } else {
      setActiveHotspot(hotspot);
    }
  };

  return (
    <section className="relative bg-black py-24 overflow-hidden">
      {/* Academy Grid Background - Shadow Branding */}
      <div 
        className="absolute inset-0 opacity-[0.03]" 
        style={{
          backgroundImage: `
            linear-gradient(90deg, transparent 49.5%, rgba(200, 200, 200, 0.5) 49.5%, rgba(200, 200, 200, 0.5) 50.5%, transparent 50.5%),
            linear-gradient(0deg, transparent 49.5%, rgba(200, 200, 200, 0.5) 49.5%, rgba(200, 200, 200, 0.5) 50.5%, transparent 50.5%)
          `,
          backgroundSize: '40px 40px'
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gray-400"></div>
            <span className="text-gray-400 font-bold text-sm tracking-[0.3em] uppercase">For Elevated Tunnel Access</span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-gray-400"></div>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight mb-4">
            STRUCTURED FOR THE <span className="bg-gradient-to-r from-gray-300 via-white to-gray-300 bg-clip-text text-transparent">FRONT OFFICE</span>
          </h2>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight mb-8" style={{ color: '#C0C0C0' }}>
            ENGINEERED FOR THE LAB
          </h3>
          <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
            We don't just sell clothes; we build the uniform for your latest elevation. 
            Every product given tunnel access is engineered with purpose.
            <span className="text-gray-300 font-semibold"> Click the hotspots to see the specs.</span>
          </p>
          <p className="text-warmSage font-black text-sm tracking-[0.3em] uppercase mt-4">SHOP HERE</p>
        </div>

        {/* Shoppable Image Container */}
        <div className="relative max-w-6xl mx-auto">
          {/* Main Image */}
          <div className="relative rounded-lg overflow-hidden border border-gray-800">
            <img
              src="https://customer-assets.emergentagent.com/job_elite-academy-app/artifacts/biewbncw_IMG_6102.jpeg"
              alt="Elevated Tunnel Access Collection - Multi-angle view"
              className="w-full h-auto"
            />
            
            {/* Chrome/Silver gradient overlay at edges */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-black/60 to-transparent"></div>
            </div>

            {/* Hotspots - Smaller, Branded Design */}
            {hotspots.map((hotspot) => (
              <div
                key={hotspot.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20"
                style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
              >
                {/* Hotspot Button - Smaller & Branded */}
                <button
                  onClick={() => handleHotspotClick(hotspot)}
                  className={`relative group ${activeHotspot?.id === hotspot.id ? 'z-30' : 'z-20'}`}
                  data-testid={`hotspot-${hotspot.id}`}
                >
                  {/* Subtle Pulse Ring */}
                  <span className="absolute inset-0 w-5 h-5 md:w-6 md:h-6 -m-0.5 rounded-full bg-warmSage/30 animate-ping"></span>
                  
                  {/* Main Button - Small Diamond/Square */}
                  <div className={`
                    relative w-4 h-4 md:w-5 md:h-5
                    flex items-center justify-center
                    transition-all duration-300 cursor-pointer
                    transform rotate-45
                    ${activeHotspot?.id === hotspot.id 
                      ? 'bg-warmSage scale-125 shadow-warmSage/50' 
                      : 'bg-gradient-to-br from-warmSage/80 to-warmSage hover:bg-warmSage hover:scale-110'
                    }
                    shadow-md shadow-black/40
                    border border-white/30
                  `}>
                    <span className={`
                      w-1.5 h-1.5 md:w-2 md:h-2 rounded-full transform -rotate-45
                      ${activeHotspot?.id === hotspot.id ? 'bg-black' : 'bg-black/70'}
                    `}></span>
                  </div>
                </button>
              </div>
            ))}
          </div>

          {/* Product Popup - Rendered Outside Image Container */}
          {activeHotspot && (
            <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4 bg-black/50" onClick={() => setActiveHotspot(null)}>
              <div 
                className="bg-black/98 backdrop-blur-md rounded-lg border border-gray-700 shadow-2xl overflow-hidden w-full max-w-md max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="p-4 border-b border-gray-800 sticky top-0 bg-black/98">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-warmSage text-[10px] font-bold tracking-[0.2em] uppercase mb-1">
                        {activeHotspot.label}
                      </p>
                      <h4 className="text-white font-black text-base md:text-lg uppercase tracking-tight">
                        {activeHotspot.name}
                      </h4>
                      <p className="text-orange-500 text-xs font-bold tracking-wider uppercase mt-1">
                        {activeHotspot.subtitle}
                      </p>
                    </div>
                    <button 
                      onClick={() => setActiveHotspot(null)}
                      className="text-gray-500 hover:text-white transition-colors flex-shrink-0 p-1"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {activeHotspot.description}
                  </p>
                  
                  {/* Feature Tag */}
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-4 bg-gradient-to-b from-warmSage to-warmSage/50 flex-shrink-0 mt-0.5"></div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider">
                      {activeHotspot.feature}
                    </p>
                  </div>

                  {/* Price & CTA */}
                  {activeHotspot.price && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-800 gap-3">
                      <p className="text-2xl font-black text-white">${activeHotspot.price}</p>
                      <Button 
                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-sm px-4 py-2 rounded-none"
                        onClick={() => window.open(activeHotspot.link, '_blank')}
                      >
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        VIEW PRODUCT
                      </Button>
                    </div>
                  )}
                </div>

                {/* Chrome accent line */}
                <div className="h-1 bg-gradient-to-r from-gray-600 via-gray-300 to-gray-600"></div>
              </div>
            </div>
          )}

          {/* Mobile Hint */}
          {isMobile && (
            <div className="text-center mt-4">
              <p className="text-gray-600 text-sm">
                Tap the <span className="inline-block w-2 h-2 bg-warmSage transform rotate-45 mx-1"></span> markers to explore
              </p>
            </div>
          )}
        </div>

        {/* Tunnel Access Granted Banner */}
        <div className="mt-12 flex items-center justify-center">
          <div className="flex items-center gap-4 md:gap-6">
            <div className="h-px w-8 md:w-16 bg-gradient-to-r from-transparent to-warmSage/50"></div>
            <p className="text-warmSage font-black text-xs md:text-base tracking-[0.3em] md:tracking-[0.4em] uppercase">
              TUNNEL ACCESS GRANTED
            </p>
            <div className="h-px w-8 md:w-16 bg-gradient-to-l from-transparent to-warmSage/50"></div>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-10 text-center px-4 md:px-0">
          <Button 
            className="bg-gradient-to-r from-gray-200 via-white to-gray-200 hover:from-orange-500 hover:via-orange-400 hover:to-orange-500 text-black hover:text-white font-black text-xs sm:text-sm md:text-lg px-4 sm:px-6 md:px-10 py-4 md:py-6 rounded-none transition-all duration-500 group shadow-lg w-full sm:w-auto max-w-full"
            onClick={() => window.location.href = '/shop'}
            data-testid="shop-tunnel-cta"
          >
            <span className="whitespace-normal sm:whitespace-nowrap text-center">SHOP THE TUNNEL, EXECUTE THE VISION</span>
            <ChevronRight className="ml-1 md:ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform inline-block flex-shrink-0" />
          </Button>
          
          {/* Sub-text */}
          <p className="text-gray-600 text-sm mt-6 max-w-lg mx-auto px-4">
            Every product given Tunnel Access is crafted for the client who understands that 
            <span className="text-gray-400"> presence is performance</span>.
          </p>
        </div>
      </div>

      {/* Chrome accent lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
    </section>
  );
};

export default ProspectCapsuleSection;
